/**
 * 营销工具路由
 */

import { checkBinding } from "@/api/joinSpark.js";

// JoinSpark 三方绑定平台类型：2 = JoinMC
const JOINSPARK_PLATFORM = 2;
const JOINSPARK_HOST = process.env.VUE_APP_JOINSPARK_HOST_URL;

/**
 * 素材制作页进入前拦截：每次进入前先查询当前团队的 JoinSpark 绑定状态，
 * 已绑定则直接打开自动登录页，不再进入绑定落地页（参考 pandaManage Nav.vue 的做法）。
 */
async function materialCreationGuard(to, from, next) {
  try {
    const store = (await import("@/store")).default;
    const userInfo = store.getters["authoState/user_info"] || {};
    const currentTeam = store.getters["authoState/current_team"] || {};
    const res = await checkBinding({
      platform: JOINSPARK_PLATFORM,
      openid: String(userInfo.id || ""),
      mobile_prefix: 86,
      mobile_number: userInfo.mobile_number || "",
      team_id: String(currentTeam.id || "")
    });
    if (res && res.code === 1 && res.data && res.data.bound) {
      window.open(`${JOINSPARK_HOST}auto-login?platform=${JOINSPARK_PLATFORM}&token=${res.data.check_token}`);
      // 已绑定，取消本次进入，保持停留在原页面。
      next(false);
      return;
    }
  } catch (error) {
    console.error(error);
  }
  next();
}
const routesAI = [{
  path: "/marketingTools",
  name: "marketingTools",
  // 默认进入 WhatsApp 群发；WhatsApp 联系人功能已迁移到独立子路由。
  redirect: "/marketingTools/whatsapp-blast",
  component: () => import("@/views/marketing/navigationMarteing.vue"),
  meta: {
    title: "route.marketing-tools",
    crumbs: "route.marketing-tools",
    isLogin: true,
    // 主侧栏直接读取 sidebar 元数据生成一级导航。
    sidebar: {
      label: "营销工具",
      labelKey: "route.marketing-tools",
      icon: "Toolkit",
      order: 70
    }
  },
  children: [{
    path: "/marketingTools/whatsapp-blast",
    name: "whatsappBlast",
    component: () => import("@/views/marketing/whatsapp-blast/whatsapp-blast.vue"),
    meta: {
      title: "route.whatsapp-blast",
      crumbs: "route.whatsapp-blast-crumb",
      isLogin: true,
      sidebar: {
        label: "WhatsApp 群发",
        labelKey: "route.whatsapp-blast",
        icon: "WhatsApp",
        order: 10
      }
    }
  }, {
    // WhatsApp 联系人的对外访问地址使用 contacts，避免继续暴露 templates 语义。
    path: "/marketingTools/whatsapp-contacts",
    component: () => import("@/views/marketing/whatsapp-contacts/whatsapp-contacts.vue"),
    name: "whatsappContacts",
    meta: {
      title: "route.whatsapp-contacts",
      crumbs: "route.whatsapp-contacts-crumb",
      isLogin: true,
      sidebar: {
        label: "WhatsApp 联系人",
        labelKey: "route.whatsapp-contacts",
        icon: "Address book",
        order: 20
      }
    }
  }, {
    path: "/marketingTools/tag-management",
    name: "tagManagement",
    component: () => import("@/views/marketing/tag-management/tag-management.vue"),
    meta: {
      title: "route.tag-management",
      crumbs: "route.tag-management-crumb",
      isLogin: true,
      sidebar: {
        label: "标签管理",
        labelKey: "route.tag-management",
        icon: "Tag",
        order: 30
      }
    }
  }, {
    // 优惠券库列表页负责展示任务汇总，并作为优惠券详情页的返回入口。
    path: "/marketingTools/coupon-library",
    name: "couponLibrary",
    component: () => import("@/views/marketing/coupon-library/coupon-library.vue"),
    meta: {
      title: "route.coupon-library",
      crumbs: "route.coupon-library-crumb",
      isLogin: true,
      // 使用产品提供的券面图标，主侧栏会自动从此元数据生成二级入口。
      sidebar: {
        label: "优惠券库",
        labelKey: "route.coupon-library",
        icon: "Coupon",
        order: 40
      }
    }
  }, {
    path: "/marketingTools/material-creation",
    name: "materialCreation",
    component: () => import("@/views/marketing/Materials/Index.vue"),
    beforeEnter: materialCreationGuard,
    meta: {
      title: "route.material-creation",
      crumbs: "route.material-creation-crumb",
      isLogin: true,
      // 使用产品提供的券面图标，主侧栏会自动从此元数据生成二级入口。
      sidebar: {
        label: "AI素材制作",
        labelKey: "route.material-creation",
        icon: "Material",
        order: 40
      }
    }
  }, {
    // 详情使用任务 ID 作为路由参数，刷新页面后仍能通过 coupon/detail 重新拉取券码明细。
    path: "/marketingTools/coupon-library/:taskId",
    name: "couponDetail",
    component: () => import("@/views/marketing/coupon-library/coupon-detail.vue"),
    meta: {
      title: "route.coupon-detail",
      crumbs: "route.coupon-detail-crumb",
      isLogin: true,
      parentName: "couponLibrary"
    }
  }, {
    path: "/marketingTools/whatsapp-blast/newTask/:taskType/:id?",
    name: "NewTask",
    component: () => import("@/views/marketing/whatsapp-blast/NewTask.vue"),
    meta: {
      title: "route.blast-template",
      isLogin: true,
      crumbs: "route.blast-template-crumb",
      needBackBtn: true,
      parentName: "MarketingTools"
    }
  },
  // =========== 邮件群发相关 start ==============
  {
    path: "/marketingTools/mass-email-list",
    name: "MassEmailList",
    component: () => import("@/views/marketing/massEmail/MassEmailList.vue"),
    meta: {
      title: "邮件群发",
      crumbs: "JoinMC - 营销工具 - 邮件群发",
      isLogin: true,
      sidebar: {
        label: "邮件群发",
        labelKey: "route.mass-email-list",
        icon: "Mail",
        order: 12
      }
    }
  }, {
    path: "/marketingTools/mass-email-list/new-task",
    name: "NewMassEmailTask",
    component: () => import("@/views/marketing/massEmail/NewMassEmailTask.vue"),
    meta: {
      title: "新建邮件群发",
      isLogin: true,
      needBackBtn: true,
      parentName: "MassEmailList"
    }
  }, {
    path: "/marketingTools/mass-email-list/task-info/:taskId",
    name: "MassEmailTaskDetail",
    component: () => import("@/views/marketing/massEmail/MassEmailTaskDetail.vue"),
    meta: {
      title: "邮件群发任务详情",
      isLogin: true,
      needBackBtn: true,
      parentName: "MassEmailList"
    }
  },
  // =========== 邮件群发相关 end ==============
  // =========== start 自动化任务 start =============
  {
    path: "/marketingTools/automate-task",
    name: "AutomateTaskList",
    component: () => import("@/views/marketing/automateTask/AutomateTaskList.vue"),
    meta: {
      isLogin: true,
      needBackBtn: false,
      title: "自动化任务",
      sidebar: {
        label: "自动化任务",
        labelKey: "route.automateTaskList",
        icon: "Automation",
        order: 13
      },
      series: "AutomateTaskList"
    }
  }, {
    path: "/marketingTools/automate-task/create",
    name: "CreateAutomateTask",
    component: () => import("@/views/marketing/automateTask/CreateAutomateTask.vue"),
    meta: {
      isLogin: true,
      title: "新建自动化任务",
      series: "AutomateTaskList"
    }
  }, {
    path: "/marketingTools/automate-task/:taskId",
    name: "AutomateStatistics",
    component: () => import("@/views/marketing/automateTask/AutomateStatistics.vue"),
    meta: {
      isLogin: true,
      title: "自动化任务数据详情",
      series: "AutomateTaskList"
    }
  }
  // =========== end 自动化任务 end =============
  ]
}];
export default routesAI;